import type { PlanTier } from '@/types';

/**
 * Single source of truth for plan pricing shown to customers. BillingPage and the wizard's
 * go-live paywall BOTH read this, so a price can never drift between the two. Prices/names here
 * must match the Stripe products behind VITE_STRIPE_PRICE_*.
 *
 * NOTE: this is a hardcoded module today. A future admin "plan pricing" screen (billing-admin
 * permission) will let staff edit these + the Stripe plans from the UI — at which point this
 * becomes server-sourced. Until then, edit here (and Stripe) together.
 */
export interface PlanDef {
  tier: PlanTier;
  name: string; // marketing name (shown to customer + on Stripe invoices)
  shortName: string; // for badges and pills, where the full name doesn't fit
  monthlyPrice: number; // $/mo
  annualPrice: number; // $/yr total
  setupFee: number; // one-time $, charged with monthly only (waived on annual)
  monthlyPriceId: string; // VITE_STRIPE_PRICE_* (monthly)
  annualPriceId: string; // VITE_STRIPE_PRICE_*_ANNUAL
  features: string[];
}

export const PLANS: PlanDef[] = [
  {
    tier: 'free',
    shortName: 'Free',
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    setupFee: 0,
    monthlyPriceId: '',
    annualPriceId: '',
    features: ['Single-page site', 'Subdomain hosting', 'Basic contact form', 'Sample listings'],
  },
  {
    tier: 'starter',
    shortName: 'Essentials',
    name: 'Agent IDX Essentials',
    monthlyPrice: 59,
    annualPrice: 708,
    setupFee: 149,
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_STARTER ?? '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_STARTER_ANNUAL ?? '',
    features: [
      'Responsive branded website',
      'Property search + IDX',
      'Listing & sold-property pages',
      'Home valuation page',
      'Lead capture + MyStateMLS CRM',
      'Hosting, SSL & basic SEO',
    ],
  },
  {
    tier: 'pro',
    shortName: 'Growth',
    name: 'Agent IDX Growth',
    monthlyPrice: 99,
    annualPrice: 1188,
    setupFee: 299,
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_PRO ?? '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL ?? '',
    features: [
      'Everything in Essentials',
      'Community & neighborhood pages',
      'School & market-report pages',
      'Featured communities',
      'Blog + AI-generated content',
      '1 hr support / month',
    ],
  },
  {
    tier: 'agency',
    shortName: 'Brokerage',
    name: 'Brokerage Growth',
    monthlyPrice: 249,
    annualPrice: 2988,
    setupFee: 999,
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_AGENCY ?? '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_AGENCY_ANNUAL ?? '',
    features: [
      'Everything in Growth',
      'Brokerage site + office pages',
      'Agent roster & profile pages',
      'Recruiting + market-coverage pages',
      'Featured agents + advanced lead routing',
      '2 hrs support / month',
    ],
  },
];

export const PLAN_ORDER: PlanTier[] = ['free', 'starter', 'pro', 'agency'];

export function planIndex(tier: PlanTier): number {
  return PLAN_ORDER.indexOf(tier);
}

export function planForTier(tier: PlanTier): PlanDef | undefined {
  return PLANS.find((p) => p.tier === tier);
}

/**
 * What a customer should be shown for a plan: the marketing name, not the internal tier.
 *
 * "Starter Plan" appeared in the Current plan header while the very card beside it, badged as
 * their current plan, said "Agent IDX Essentials". `starter`/`pro`/`agency` are database values
 * — every customer-facing surface should read this instead, so the product calls a plan one thing
 * in one voice. Staff screens (admin lists, payment tables) keep the raw tier on purpose: there
 * the database value IS the useful one.
 *
 * Falls back to the tier if it is one we don't have a definition for, which beats rendering blank.
 */
export function planLabel(tier: PlanTier | string | null | undefined): string {
  if (!tier) return 'Free';
  return PLANS.find((p) => p.tier === tier)?.name ?? String(tier);
}

/** The same idea where space is tight — a header pill, a badge. "Essentials", not "starter". */
export function planShortLabel(tier: PlanTier | string | null | undefined): string {
  if (!tier) return 'Free';
  return PLANS.find((p) => p.tier === tier)?.shortName ?? String(tier);
}

/** The cheapest PAID plan — the entry point for "go live" (any paid plan unlocks it). */
export function entryPaidPlan(): PlanDef {
  return PLANS.find((p) => p.monthlyPrice > 0) ?? PLANS[1];
}
